import { useEffect, useMemo, useRef, useState } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import Gallery from "./components/videoplayer";

/* -------------------- Config -------------------- */
const CONFIG = {
  clientId: process.env.REACT_APP_AAD_CLIENT_ID,
  tenantId: process.env.REACT_APP_AAD_TENANT_ID || "organizations",
  redirectUri:
    process.env.REACT_APP_AAD_REDIRECT_URI || window.location.origin,
  apiScopes: (
    process.env.REACT_APP_FUNCTION_APP_AAD_SCOPE ||
    process.env.REACT_APP_AAD_SCOPE ||
    process.env.REACT_APP_AAD_GRAPH_SCOPE ||
    "User.Read"
  )
    .split(/[,\s]+/)
    .map((scope) => scope.trim())
    .filter(Boolean),
};

/* -------------------- Helpers -------------------- */
const getQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  const accessToken =
    params.get("AccessToken") ||
    params.get("access_Token") ||
    params.get("accessToken");

  return {
    accessToken,
    workNo: params.get("WorkOrderNumber"),
    woliNo: params.get("WorkOrderLineItemNumber"),
    saNo: params.get("ServiceAppointmentNumber"),
  };
};

const cleanUrl = () => {
  // ✅ remove query params, keep path
  window.history.replaceState({}, "", window.location.pathname);
};

/* -------------------- Component -------------------- */
const App = () => {
  const msalRef = useRef(null);
  const authStartedRef = useRef(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState("");
  const [ssoAccessToken, setSsoAccessToken] = useState("");

  const queryParams = useMemo(getQueryParams, []);
  const resolvedAccessToken = queryParams.accessToken || ssoAccessToken;

  const searchApiUrl = process.env.REACT_APP_FUNCTION_APP_SEARCH_API_URL;
  const videoindexerAccountId =
    process.env.REACT_APP_VIDEO_INDEXER_ACCOUNT_ID;
  const videoindexerLocation =
    process.env.REACT_APP_VIDEO_INDEXER_LOCATION;

  /* -------------------- Authentication -------------------- */
  useEffect(() => {
    if (authStartedRef.current) return;
    authStartedRef.current = true;

    const authenticate = async () => {
      if (!CONFIG.clientId) {
        setAuthError("App configuration error.");
        return;
      }

      const msal = new PublicClientApplication({
        auth: {
          clientId: CONFIG.clientId,
          authority: `https://login.microsoftonline.com/${CONFIG.tenantId}`,
          redirectUri: CONFIG.redirectUri,
        },
        cache: { cacheLocation: "sessionStorage" },
      });

      msalRef.current = msal;

      try {
        await msal.initialize();

        const redirectResult = await msal.handleRedirectPromise();
        let account =
          redirectResult?.account || msal.getAllAccounts()[0];

        /* ✅ Fast login (no ssoSilent) */
        if (!account) {
          await msal.loginRedirect({
            scopes: CONFIG.apiScopes,
          });
          return;
        }

        /* ✅ Tenant validation */
        const expectedTenant =
          !["organizations", "common", "consumers"].includes(
            CONFIG.tenantId.toLowerCase()
          )
            ? CONFIG.tenantId.toLowerCase()
            : null;

        if (
          expectedTenant &&
          (account.tenantId || "").toLowerCase() !== expectedTenant
        ) {
          throw new Error("Unauthorized organization.");
        }

        /* ✅ Token with fallback */
        try {
          const tokenResult = await msal.acquireTokenSilent({
            account,
            scopes: CONFIG.apiScopes,
          });

          setSsoAccessToken(tokenResult.accessToken);
        } catch {
          await msal.acquireTokenRedirect({
            account,
            scopes: CONFIG.apiScopes,
          });
          return;
        }

        setIsAuthenticated(true);

        /* ✅ IMPORTANT: clean URL AFTER reading token */
        cleanUrl();
      } catch (err) {
        console.error(err);
        setAuthError("Authentication failed. Please try again.");
      }
    };

    authenticate();
  }, []);

  /* -------------------- UI -------------------- */
  if (authError) {
    return (
      <div className="authGate">
        <div className="authCard">
          <h3>Sign-in Error</h3>
          <p>{authError}</p>
          <button onClick={() => window.location.reload()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="authGate">
        <div className="authCard">
          <h3>Signing you in...</h3>
          <p>Please wait while we authenticate your account.</p>
        </div>
      </div>
    );
  }

  /* -------------------- Main App -------------------- */
  return (
    <Gallery
      {...queryParams} 
      accessToken={resolvedAccessToken}
      searchApiUrl={searchApiUrl}
      videoindexerAccountId={videoindexerAccountId}
      videoindexerLocation={videoindexerLocation}
    />
  );
};

export default App;